import {
  Button,
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  Icons,
  List,
  notification,
  Space,
  Table,
  TableProps,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useNavigation } from '@pankod/refine-core';
import { SearchNew } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import { ICategory, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { RiTodoLine } from 'react-icons/ri';
import { getAllCategory, getAllCategoryDropdown } from 'services/category';
import { SearchIndex } from 'services/meilisearch';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';
import styles from './style.module.css';

export const CategoryList: React.FC<IResourceComponentsProps> = () => {
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [totalCategories, setTotalCategories] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);
  const { push } = useNavigation();

  const fetchDataOnMount = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllCategory({ searchString: searchText, start: page, limit });
      const categoriesData = data.data;

      setCategories(categoriesData);
      setTotalCategories(data.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [limit, page, searchText]);

  useEffect(() => {
    fetchDataOnMount();
  }, [fetchDataOnMount]);

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onDeleteSuccessHandler = async () => {
    try {
      const data = await getAllCategory();
      const categoriesData = data.data;
      setCategories(categoriesData);
      setTotalCategories(data.total);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const categoriesData = await getAllCategoryDropdown();
      const rows = [['Name', 'Description']];
      categoriesData.forEach((element) => {
        rows.push([element.name, element.description]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeaders = (
    <div className={styles['page-header__actions']}>
      <CreateButton type="primary" />
      <Button
        size="middle"
        icon={<Icons.ImportOutlined />}
        onClick={() => push('/categories/import')}
      >
        Import
      </Button>
      <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick} />
      <Button
        icon={<Icons.HistoryOutlined />}
        onClick={() => push(getActivityHistoryUrl('category'))}
      >
        Activity
      </Button>
    </div>
  );

  const onTableChange: TableProps<ICategory>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra,
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <List headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <SearchNew
        placeholder="Search by Name"
        size="large"
        className={styles.search}
        index={SearchIndex.Category}
        groups={['name']}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMount}
      />
      <CustomTableWithPagination
        total={totalCategories}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={categories}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          // pagination={{
          //   defaultPageSize: 10,
          //   showSizeChanger: true,
          //   total: totalCategories,
          //   current: page,
          // }}
          pagination={false}
        >
          <Table.Column<ICategory> dataIndex="name" title="Name" />
          <Table.Column dataIndex="description" title="Description" />
          <Table.Column<ICategory>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <Button
                    size="small"
                    icon={<RiTodoLine size={18} style={{ paddingTop: '4px' }} />}
                    onClick={(e) => {
                      e.preventDefault();
                      const url = `/inventories?category=${encodeURIComponent(record.name)}`;
                      push(url);
                    }}
                  />
                  <EditButton size="small" recordItemId={record.id} hideText />
                  <DeleteButton
                    size="small"
                    hideText
                    recordItemId={record.id}
                    onSuccess={onDeleteSuccessHandler}
                  />
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};
